import React, { useEffect, useRef, useState } from "react";
import { Restaurant } from "../../../interfaces/Catalogue";
import { useConfig } from "../../../context/ConfigContext";
import RestaurantSelectionModal from "../../common/RestaurantSelectionModal/RestaurantSelectionModal";
import "./RestaurantsMapSection.scss";
import appConfig from "../../../appConfig";
import { buildIconWithBg } from "../../../utils/iconUtils";

declare global {
  interface Window {
    google: typeof google;
    initRestaurantsMap?: () => void;
  }
}

interface Props {
  restaurants: Restaurant[];
  selectedRestaurant?: Restaurant | null;
  onSelectRestaurant: (restaurant: Restaurant) => void;
}

// ── Icons (inline SVG to avoid extra deps) ───────────────
const IconMapPin = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const IconClock = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconStar = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87
             1.18 6.88L12 17.77l-6.18 3.25
             1.18-6.88-5-4.87 6.91-1.01z"/>
  </svg>
);

const IconCheck = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconNavigation = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="3 11 22 2 13 21 11 13 3 11" />
  </svg>
);

const RestaurantsMapSection: React.FC<Props> = ({
  restaurants,
  selectedRestaurant,
  onSelectRestaurant,
}) => {
  const { config } = useConfig();
  const primaryColor = config?.primaryColor || "#6366F1";
  const logoUrl = config?.appLogoImage || "";

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const [mapsReady, setMapsReady] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  // ── Load Google Maps script once ────────────────────────
  useEffect(() => {
    if (window.google?.maps) { setMapsReady(true); return; }
    if (!appConfig.GOOGLE_MAPS_API_KEY) return;

    window.initRestaurantsMap = () => setMapsReady(true);

    const existing = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existing) return;

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${appConfig.GOOGLE_MAPS_API_KEY}&callback=initRestaurantsMap`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => { window.initRestaurantsMap = undefined; };
  }, []);

  // ── Resolve lat/lng ──────────────────────────────────────
  const resolveLatLng = (restaurant: Restaurant): Promise<google.maps.LatLngLiteral | null> => {
    if (restaurant.latitude !== 0 && restaurant.longitude !== 0) {
      return Promise.resolve({ lat: restaurant.latitude, lng: restaurant.longitude });
    }
    if (!restaurant.address) return Promise.resolve(null);
    return new Promise((resolve) => {
      new window.google.maps.Geocoder().geocode(
        { address: restaurant.address! },
        (results, status) => {
          resolve(status === "OK" && results?.[0]
            ? { lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() }
            : null
          );
        }
      );
    });
  };

  // ── Build map + markers ──────────────────────────────────
  useEffect(() => {
    if (!mapsReady || !mapRef.current || !restaurants.length) return;

    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 12,
      center: { lat: 48.8566, lng: 2.3522 },
      disableDefaultUI: true,
      zoomControl: true,
      styles: [
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "off" }] },
      ],
    });
    mapInstanceRef.current = map;

    const sharedInfoWindow = new window.google.maps.InfoWindow();
    infoWindowRef.current = sharedInfoWindow;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const fallbackIcon: google.maps.Symbol = {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 14,
      fillColor: primaryColor,
      fillOpacity: 1,
      strokeColor: "#fff",
      strokeWeight: 2,
    };

    const placeAllMarkers = async () => {
      const iconDataUrl = logoUrl ? await buildIconWithBg(logoUrl, config?.layoutColor) : null;
      const markerIcon: google.maps.Icon | undefined = iconDataUrl
        ? { url: iconDataUrl, scaledSize: new window.google.maps.Size(40, 40), anchor: new window.google.maps.Point(20, 40) }
        : undefined;

      const positions = await Promise.all(restaurants.map((r) => resolveLatLng(r)));
      const bounds = new window.google.maps.LatLngBounds();
      let hasAnyMarker = false;

      restaurants.forEach((restaurant, i) => {
        const position = positions[i];
        if (!position) return;

        hasAnyMarker = true;
        bounds.extend(position);

        const marker = new window.google.maps.Marker({
          position, map,
          title: restaurant.nom,
          icon: markerIcon ?? fallbackIcon,
          animation: window.google.maps.Animation.DROP,
        });

        markersRef.current.push(marker);

        marker.addListener("click", () => {
          const isSelected = selectedRestaurant?.id === restaurant.id;
          const isOpen = restaurant.isOpen;

          sharedInfoWindow.setContent(`
            <div style="font-family:system-ui,sans-serif;padding:4px 2px;min-width:180px;">
              <p style="font-size:14px;font-weight:700;color:#111827;margin:0 0 4px;">
                ${restaurant.nom}
              </p>

              ${restaurant.address
              ? `<p style="font-size:12px;color:#6b7280;margin:0 0 8px;">📍 ${restaurant.address}</p>`
              : ""
            }

              <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
                <span style="
                  display:inline-flex;
                  align-items:center;
                  gap:4px;
                  font-size:11px;
                  font-weight:600;
                  padding:2px 8px;
                  border-radius:6px;
                  background:${isOpen ? "#d1fae5" : "#fee2e2"};
                  color:${isOpen ? "#059669" : "#dc2626"};
                ">
                  ${isOpen ? "● Ouvert" : "● Fermé"}
                </span>

                ${restaurant.tempsPrep > 0
              ? `<span style="font-size:11px;color:#6b7280;">⏱ ${restaurant.tempsPrep}min</span>`
              : ""
            }
              </div>

              ${!isSelected
              ? `
                    <button
                      id="iw-select-${restaurant.id}"
                      type="button"
                      style="
                        width:100%;
                        padding:8px 12px;
                        border:none;
                        border-radius:8px;
                        background:${primaryColor};
                        color:#ffffff !important;
                        -webkit-text-fill-color:#ffffff;
                        font-size:13px;
                        font-weight:600;
                        line-height:1.2;
                        cursor:pointer;
                        appearance:none;
                        -webkit-appearance:none;
                      "
                    >
                      <span style="color:#ffffff !important;-webkit-text-fill-color:#ffffff;">
                        Sélectionner ce restaurant
                      </span>
                    </button>
                  `
              : `
                    <p style="
                      text-align:center;
                      font-size:12px;
                      font-weight:600;
                      color:${primaryColor};
                      margin:0;
                    ">
                      ✓ Restaurant sélectionné
                    </p>
                  `
            }
            </div>
          `);

          sharedInfoWindow.open(map, marker);

          window.google.maps.event.addListenerOnce(sharedInfoWindow, "domready", () => {
            document.getElementById(`iw-select-${restaurant.id}`)?.addEventListener("click", () => {
              onSelectRestaurant(restaurant);
              sharedInfoWindow.close();
            });
          });
        });
      });

      if (hasAnyMarker) {
        map.fitBounds(bounds);
        window.google.maps.event.addListenerOnce(map, "idle", () => {
          if ((map.getZoom() ?? 0) > 15) map.setZoom(15);
        });
      }
    };

    placeAllMarkers();
  }, [mapsReady, restaurants, selectedRestaurant, primaryColor, logoUrl]);

  // ── Pan map to a restaurant on list-item click ───────────
  const handleListItemClick = (restaurant: Restaurant) => {
    onSelectRestaurant(restaurant);
    if (mapInstanceRef.current && restaurant.latitude && restaurant.longitude) {
      mapInstanceRef.current.panTo({ lat: restaurant.latitude, lng: restaurant.longitude });
      mapInstanceRef.current.setZoom(15);
    }
  };

  if (!appConfig.GOOGLE_MAPS_API_KEY) return null;

  // First restaurant = nearest (index 0)
  const nearestId = restaurants[0]?.id;

  return (
    <div className="rmap-root">
      {/* ── Header ── */}
      <div className="rmap-header">
        <span className="rmap-title"> {restaurants.length > 1 ? "Nos restaurants" : "Notre restaurant"}

        </span>
        {restaurants.length > 1 &&

          <button className="rmap-list-btn" onClick={() => setModalVisible(true)}>
            Voir la liste
          </button>
        }
      </div>

      {/* ── Map ── */}
      <div className="rmap-map-wrap">
        {!mapsReady && (
          <div className="rmap-loading">
            <span className="rmap-spinner" />
          </div>
        )}
        <div ref={mapRef} className="rmap-canvas" />
      </div>

      {/* ── Restaurant List ── */}
      {restaurants.length > 1 && (
        <div className="rmap-list">
          {restaurants.map((restaurant, index) => {
            const isSelected = selectedRestaurant?.id === restaurant.id;
            const isNearest = restaurant.id === nearestId;
            const isOpen = restaurant.isOpen;

            return (
              <React.Fragment key={restaurant.id}>
                {index > 0 && <div className="rmap-divider" />}

                <div
                  className={`rmap-item${!isOpen ? " rmap-item--closed" : ""}`}
                  onClick={() => handleListItemClick(restaurant)}
                >
                  <div className="rmap-item-body">
                    {/* Tags */}
                    {(isNearest || isSelected) && (
                      <div className="rmap-item-tags">
                        {isNearest && (
                          <span className="rmap-tag rmap-tag--nearest">
                            <IconNavigation />
                            Le plus proche
                          </span>
                        )}
                        {isSelected && (
                          <span
                            className="rmap-tag rmap-tag--selected"
                            style={{ background: primaryColor }}
                          >
                            Sélectionné
                          </span>
                        )}
                      </div>
                    )}

                    {/* Name */}
                    <p className="rmap-item-name">{restaurant.nom}</p>

                    {/* Address */}
                    {restaurant.address && (
                      <p className="rmap-item-addr">
                        <IconMapPin />
                        {restaurant.address}
                      </p>
                    )}

                    {/* Meta chips */}
                    <div className="rmap-item-meta">
                      {/* Open / Closed badge */}
                      <span className={`rmap-badge rmap-badge--${isOpen ? "open" : "closed"}`}>
                        <span className="rmap-badge-dot" />
                        {isOpen ? "Ouvert" : "Fermé"}
                      </span>

                      {/* Prep time */}
                      {restaurant.tempsPrep > 0 && (
                        <span className="rmap-chip">
                          <IconClock />
                          {restaurant.tempsPrep} min
                        </span>
                      )}

                      {/* Rating */}
                      {/* {(restaurant?.rating || 0) > 0 && (
                        <span className="rmap-chip rmap-chip--star">
                          <IconStar />
                          {restaurant?.rating.toFixed(1)}
                        </span>
                      )} */}
                    </div>
                  </div>

                  {/* Checkmark if selected */}
                  {isSelected && (
                    <div className="rmap-check" style={{ background: primaryColor }}>
                      <IconCheck />
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* ── Modal ── */}
      <RestaurantSelectionModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSelect={(r) => {
          onSelectRestaurant(r);
          setModalVisible(false);
        }}
      />
    </div>
  );
};

export default RestaurantsMapSection;